import { DeviceFrameSpec } from "components/elements/DeviceFrame"
import type { PreviewDevice } from "./kioskPreviewTypes"

/**
 * Kiosk preview layout model.
 *
 * Mirrors the iOS kiosk implementation (v6.18.0). Every measurement in the spec is
 * a point value (CSS px @ 1x) relative to the device's content area. Here we encode
 * those point values verbatim, then convert to preview pixels via a single `scale`
 * (preview-px per point) so the on-screen proportions match iOS exactly regardless of
 * how large the preview frame is rendered.
 *
 * Two variants, picked by device:
 *   - regular → iPad (portrait + landscape)
 *   - compact → iPhone (portrait only)
 */

export type KioskVariant = "regular" | "compact"
export type KioskOrientation = "portrait" | "landscape"

/** Per-device content model, in points (CSS px @ 1x density). */
type DeviceModel = {
  variant: KioskVariant
  orientation: KioskOrientation
  screenWPt: number
  screenHPt: number
  /** Status-bar inset at the top of the screen (excluded from the content area). */
  statusTopPt: number
  /** Home-indicator inset at the bottom of the screen (excluded from the content area). */
  homeBottomPt: number
}

const DEVICE_MODELS: Record<PreviewDevice, DeviceModel> = {
  iphone: {
    variant: "compact",
    orientation: "portrait",
    screenWPt: 402,
    screenHPt: 874,
    statusTopPt: 59,
    homeBottomPt: 34,
  },
  ipadPortrait: {
    variant: "regular",
    orientation: "portrait",
    screenWPt: 834,
    screenHPt: 1210,
    statusTopPt: 24,
    homeBottomPt: 20,
  },
  ipadLandscape: {
    variant: "regular",
    orientation: "landscape",
    screenWPt: 1210,
    screenHPt: 834,
    statusTopPt: 24,
    homeBottomPt: 20,
  },
}

/**
 * Bottom reservation (chrome bar + gap) that the pre-scan and scan layouts treat as
 * off-limits. The iOS chrome bar is ~50pt (iPad) / ~70pt (iPhone, incl. home indicator),
 * with a 32pt gap above. We don't render a chrome bar, so we reserve the equivalent strip
 * to keep proportions identical. The home-indicator inset is already removed from the
 * content area, so on iPhone we subtract it back out of the 70pt figure.
 */
const RESERVED_GAP_PT = 32
const RESERVED_CHROME_PT: Record<KioskVariant, number> = {
  regular: 50,
  compact: 70 - 34, // 70pt incl. home indicator, which is already excluded from content
}

/** Pre-scan (home) spec, in points. */
const HOME = {
  sideMargin: { regular: 15, compact: 16 },
  logoTitleGap: 24,
  // Logo heights. The "hidden" value is sized so the brand block occupies the same
  // vertical band whether or not the title is shown (hidden = shown + gap + titleFont).
  logo: {
    regular: { shown: 120, hidden: 234 },
    compact: { shown: 66, hidden: 118 },
  },
  // regular tuned to the actual iPad result (spec nominal is 90; trimmed per review).
  titleFont: { regular: 78, compact: 28 },
  button: {
    diameter: { regular: 260, compactOne: 200, compactTwo: 150 },
    // Bumped above the raw spec pt values: our glyphs have more internal padding than
    // the iOS SF Symbols, so they render visually smaller at the nominal size.
    icon: { regular: 74, compactOne: 54, compactTwo: 40 },
    titleFont: { regular: 40, compactOne: 27, compactTwo: 19 },
    iconTitleGap: 12,
    baseInset: 16,
    hInsetExtra: { regular: 24, compact: 16 },
    vInset: 16,
    gap: { regular: 80, compact: 24 },
    outerMargin: { regular: 24, compact: 16 },
  },
  // topGap : midGap : bottomGap
  spacerRatio: { top: 1, mid: 2.5, bottom: 2.5 },
} as const

/** Scan spec, in points. */
const SCAN = {
  sideMargin: { regular: 20, compact: 16 },
  labelFont: { regular: 42, compact: 28 },
  labelGap: { regular: 40, compact: 24 },
  minVPad: 24,
  cameraRadius: 12,
  cameraMaxDominant: 440,
  targetRatio: 0.55,
  targetCap: 148,
  header: {
    pad: 16,
    logoShown: { regular: 60, compact: 40 },
    stackHidden: { regular: 100, compact: 70 },
    gap: { regular: 16, compact: 12 },
    titleFont: { regular: 24, compact: 18 },
    backGlyph: { regular: 46, compact: 32 },
  },
} as const

export type KioskHomeLayout = {
  /** Absolute box (px) for the brand block, measured from the screen-region top-left. */
  brand: {
    top: number
    left: number
    width: number
    height: number
    hasLogo: boolean
    hasTitle: boolean
    logoHeight: number
    titleTop: number
    titleHeight: number
    titleFontPx: number
  }
  /** Absolute box (px) for the centered button row. */
  buttons: {
    top: number
    height: number
    diameter: number
    gap: number
    iconPx: number
    titleFontPx: number
    hInset: number
    vInset: number
    iconTitleGap: number
    /** Register (outlined) button stroke, 4pt. */
    strokePx: number
  }
}

export type KioskScanLayout = {
  header: {
    /** Full bar height (px) from the screen top — extends up through the status bar. */
    barHeight: number
    contentTop: number
    stackHeight: number
    hasLogo: boolean
    hasTitle: boolean
    logoHeight: number
    gap: number
    titleFontPx: number
    sideMargin: number
    back: { glyphPx: number; centerY: number }
  }
  group: {
    top: number
    width: number
    labelFontPx: number
    labelHeight: number
    labelGap: number
  }
  camera: {
    width: number
    height: number
    radius: number
    targetSize: number
  }
}

export type KioskLayout = {
  scale: number
  screen: { width: number; height: number }
  content: { top: number; bottom: number; height: number; width: number }
  variant: KioskVariant
  orientation: KioskOrientation
  home: KioskHomeLayout
  scan: KioskScanLayout
}

export type KioskLayoutInput = {
  device: PreviewDevice
  spec: DeviceFrameSpec
  frameWidthPx: number
  hasLogo: boolean
  hasTitle: boolean
  scanQrCodeEnabled: boolean
  walkInRegistrationEnabled: boolean
  /** Lines the home title actually occupies (1–2); reserves the brand band height. Default 1. */
  titleLines?: number
  /** Lines the scan label actually occupies (1–2); reserves its height in the group. Default 1. */
  scanLabelLines?: number
}

/** Single ~0.35 px-per-point scale derived from the rendered preview screen width. */
const computeScale = (spec: DeviceFrameSpec, frameWidthPx: number, screenWPt: number) => {
  const screenWidthPx = (frameWidthPx * spec.screen.width) / spec.frameSize.width

  return screenWidthPx / screenWPt
}

export const buildKioskLayout = ({
  device,
  spec,
  frameWidthPx,
  hasLogo,
  hasTitle,
  scanQrCodeEnabled,
  walkInRegistrationEnabled,
  titleLines = 1,
  scanLabelLines = 1,
}: KioskLayoutInput): KioskLayout => {
  const model = DEVICE_MODELS[device]
  const { variant, orientation } = model
  const scale = computeScale(spec, frameWidthPx, model.screenWPt)
  const pt = (value: number) => value * scale

  const screen = {
    width: (frameWidthPx * spec.screen.width) / spec.frameSize.width,
    height: (frameWidthPx * spec.screen.height) / spec.frameSize.width,
  }

  const contentTop = pt(model.statusTopPt)
  const contentBottom = screen.height - pt(model.homeBottomPt)
  const content = {
    top: contentTop,
    bottom: contentBottom,
    height: contentBottom - contentTop,
    width: screen.width,
  }

  const reservedPx = pt(RESERVED_CHROME_PT[variant] + RESERVED_GAP_PT)

  return {
    scale,
    screen,
    content,
    variant,
    orientation,
    home: buildHome({
      model,
      scale,
      pt,
      content,
      reservedPx,
      hasLogo,
      hasTitle,
      scanQrCodeEnabled,
      walkInRegistrationEnabled,
      titleLines,
      scanLabelLines,
    }),
    scan: buildScan({
      model, scale, pt, content, reservedPx, hasLogo, hasTitle, scanLabelLines,
    }),
  }
}

type BuildArgs = {
  model: DeviceModel
  scale: number
  pt: (v: number) => number
  content: KioskLayout["content"]
  reservedPx: number
  hasLogo: boolean
  hasTitle: boolean
  scanQrCodeEnabled: boolean
  walkInRegistrationEnabled: boolean
  titleLines: number
  scanLabelLines: number
}

const buildHome = ({
  model,
  pt,
  content,
  reservedPx,
  hasLogo,
  hasTitle,
  scanQrCodeEnabled,
  walkInRegistrationEnabled,
  titleLines,
}: BuildArgs): KioskHomeLayout => {
  const { variant } = model
  const availHeight = content.height - reservedPx

  // Brand block — always occupies the "hidden" logo band when a logo is present, so the
  // composition doesn't shift when the title is toggled.
  const logo = HOME.logo[variant]
  const brandTitleFontPt = HOME.titleFont[variant]
  const titleVisible = hasTitle
  const sideMargin = pt(HOME.sideMargin[variant])
  const brandWidth = content.width - 2 * sideMargin

  // Brand block height: with a logo it occupies the "hidden" logo band (logo + gap + one
  // title line); with no logo it shrinks to the title band; it collapses to 0 when neither
  // is shown. A wrapped title (titleLines > 1) reserves the extra line(s) so the second line
  // can't overflow into the button row.
  const titleLineCount = titleVisible ? Math.max(titleLines, 1) : 1
  const extraTitlePt = brandTitleFontPt * (titleLineCount - 1)
  let brandHeight = 0
  if (hasLogo) {
    brandHeight = pt(logo.hidden + extraTitlePt)
  } else if (titleVisible) {
    brandHeight = pt(brandTitleFontPt * titleLineCount)
  }

  const logoDrawHeight = hasLogo ? pt(titleVisible ? logo.shown : logo.hidden) : 0
  // Title sits below the logo when present, otherwise fills the brand block.
  const titleTop = hasLogo ? pt(logo.shown + HOME.logoTitleGap) : 0

  // Button row.
  const n = [scanQrCodeEnabled, walkInRegistrationEnabled].filter(Boolean).length
  const two = n >= 2
  const diameterPt = variant === "regular"
    ? HOME.button.diameter.regular
    : two ? HOME.button.diameter.compactTwo : HOME.button.diameter.compactOne
  const iconPt = variant === "regular"
    ? HOME.button.icon.regular
    : two ? HOME.button.icon.compactTwo : HOME.button.icon.compactOne
  const titleFontPt = variant === "regular"
    ? HOME.button.titleFont.regular
    : two ? HOME.button.titleFont.compactTwo : HOME.button.titleFont.compactOne
  const gap = pt(HOME.button.gap[variant])
  const outerMargin = pt(HOME.button.outerMargin[variant])
  const hInset = pt(HOME.button.baseInset + HOME.button.hInsetExtra[variant])

  // Diameter is a preferred width; shrink (keeping circles) if the row can't fit.
  let diameter = pt(diameterPt)
  if (n > 0) {
    const maxRowWidth = content.width - 2 * outerMargin
    const maxDiameter = (maxRowWidth - (n - 1) * gap) / n
    diameter = Math.min(diameter, maxDiameter)
  }
  const buttonRowHeight = n > 0 ? diameter : 0

  // Spacers: topGap : midGap : bottomGap = 1 : 2.5 : 2.5
  const { top, mid, bottom } = HOME.spacerRatio
  const totalSpacer = Math.max(availHeight - brandHeight - buttonRowHeight, 0)
  const unit = totalSpacer / (top + mid + bottom)
  const topGap = unit * top
  const midGap = unit * mid

  const brandTop = content.top + topGap
  const buttonsTop = brandTop + brandHeight + midGap

  return {
    brand: {
      top: brandTop,
      left: sideMargin,
      width: brandWidth,
      height: brandHeight,
      hasLogo,
      hasTitle: titleVisible,
      logoHeight: logoDrawHeight,
      titleTop,
      titleHeight: Math.max(brandHeight - titleTop, 0),
      titleFontPx: pt(brandTitleFontPt),
    },
    buttons: {
      top: buttonsTop,
      height: buttonRowHeight,
      diameter,
      gap,
      iconPx: pt(iconPt),
      titleFontPx: pt(titleFontPt),
      hInset,
      vInset: pt(HOME.button.vInset),
      iconTitleGap: pt(HOME.button.iconTitleGap),
      strokePx: pt(4),
    },
  }
}

type ScanBuildArgs = Omit<BuildArgs, "scanQrCodeEnabled" | "walkInRegistrationEnabled" | "titleLines">

const buildScan = ({
  model, pt, content, reservedPx, hasLogo, hasTitle, scanLabelLines,
}: ScanBuildArgs): KioskScanLayout => {
  const { variant, orientation } = model

  // Header bar.
  const titleVisible = hasTitle
  const logoShown = pt(SCAN.header.logoShown[variant])
  const stackHiddenPt = SCAN.header.stackHidden[variant]
  const stackHeight = hasLogo
    ? pt(stackHiddenPt)
    : titleVisible ? pt(SCAN.header.titleFont[variant]) : 0
  const headerPad = pt(SCAN.header.pad)
  const headerContentTop = content.top + headerPad
  const barHeight = content.top + headerPad + stackHeight + headerPad
  const sideMargin = pt(SCAN.sideMargin[variant])
  const backGlyph = pt(SCAN.header.backGlyph[variant])

  // Scan area: below the header (+32pt gap), above the bottom reservation.
  const HEADER_TO_SCAN_GAP_PT = 32
  const scanAreaTop = barHeight + pt(HEADER_TO_SCAN_GAP_PT)
  const scanAreaBottom = content.bottom - reservedPx
  const scanAreaHeight = scanAreaBottom - scanAreaTop

  // Label + camera group. Reserve height for however many lines the label wraps onto so a
  // long localized prompt isn't truncated or pushed over the camera box.
  const groupWidth = content.width - 2 * sideMargin
  const labelFontPx = pt(SCAN.labelFont[variant])
  const labelHeight = labelFontPx * 1.2 * Math.max(scanLabelLines, 1)
  const labelGap = pt(SCAN.labelGap[variant])
  const aspectHW = orientation === "portrait" ? 4 / 3 : 3 / 4
  const minVPad = pt(SCAN.minVPad)

  const maxGroupHeight = scanAreaHeight - 2 * minVPad
  let cameraWidth = groupWidth
  let cameraHeight = cameraWidth * aspectHW
  const cameraRoom = maxGroupHeight - labelHeight - labelGap
  if (cameraHeight > cameraRoom) {
    cameraHeight = Math.max(cameraRoom, 0)
    cameraWidth = cameraHeight / aspectHW
  }
  // Cap the dominant dimension (width in portrait, height in landscape) at 440pt.
  const capPx = pt(SCAN.cameraMaxDominant)
  if (orientation === "portrait" && cameraWidth > capPx) {
    cameraWidth = capPx
    cameraHeight = cameraWidth * aspectHW
  } else if (orientation === "landscape" && cameraHeight > capPx) {
    cameraHeight = capPx
    cameraWidth = cameraHeight / aspectHW
  }

  const groupHeight = labelHeight + labelGap + cameraHeight
  const groupTop = scanAreaTop + Math.max(minVPad, (scanAreaHeight - groupHeight) / 2)

  const targetSize = Math.min(
    Math.min(cameraWidth, cameraHeight) * SCAN.targetRatio,
    pt(SCAN.targetCap),
  )

  return {
    header: {
      barHeight,
      contentTop: headerContentTop,
      stackHeight,
      hasLogo,
      hasTitle: titleVisible,
      logoHeight: hasLogo ? (titleVisible ? logoShown : stackHeight) : 0,
      gap: pt(SCAN.header.gap[variant]),
      titleFontPx: pt(SCAN.header.titleFont[variant]),
      sideMargin,
      back: { glyphPx: backGlyph, centerY: headerContentTop + stackHeight / 2 },
    },
    group: {
      top: groupTop,
      width: groupWidth,
      labelFontPx,
      labelHeight,
      labelGap,
    },
    camera: {
      width: cameraWidth,
      height: cameraHeight,
      radius: pt(SCAN.cameraRadius),
      targetSize,
    },
  }
}
